import React from 'react';
import './AIPreviewModal.css';
import LoadingSpinner from '../LoadingSpinner';
import { useTranslation } from '../../i18n';
import Icon from '../Icon';
import Modal from '../Modal';

interface AIPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (title: string, description: string) => void;
  title: string;
  description: string;
  originalMessage: string;
  isLoading?: boolean;
}

const AIPreviewModal: React.FC<AIPreviewModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  title,
  description,
  originalMessage,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const handleAccept = () => {
    onAccept(title, description);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      bare
      isOpen={isOpen}
      onClose={onClose}
      disableClose={isLoading}
      className="modal-backdrop"
      contentClassName="ai-preview-modal"
    >
      <div className="modal-header">
        <div>
          <h3>
            <span className="modal-icon"><Icon name="sparkles" /></span>
            {t('ai.previewModal.title')}
          </h3>
        </div>
        <button 
          className="modal-close-btn"
          onClick={onClose}
          disabled={isLoading}
          aria-label={t('common.close')}
          title={t('common.close')}
        >
          <Icon name="close" />
        </button>
      </div>

      <div className="ai-modal-content">
        {isLoading ? (
          <div className="loading-container-2">
            <LoadingSpinner size="large" color="primary" hideMessage />
            <p className="loading-message">{t('ai.previewModal.loading.message')}</p>
            <p className="loading-submessage">{t('ai.previewModal.loading.wait')}</p>
          </div>
        ) : (
          <>
            <div className="ai-content-wrapper">
              <div className="original-message">
                <label>{t('ai.previewModal.labels.yourMessage')}</label>
                <div className="message-content">{originalMessage}</div>
              </div>

              <div className="ai-suggestion">
                <div className="suggestion-section">
                  <label>{t('ai.previewModal.labels.suggestedTitle')}</label>
                  <div className="suggestion-title">{title}</div>
                </div>

                <div className="suggestion-section">
                  <label>{t('ai.previewModal.labels.suggestedDescription')}</label>
                  <div className="suggestion-description">
                    {description.split('\n').map((line, index) => (
                      <div key={index} className="description-line">
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="ai-badge">
                <span className="ai-icon"><Icon name="sparkles" /></span>
                {t('ai.previewModal.badge')}
              </div>
            </div>
          </>
        )}
      </div>
      {!isLoading && (
          <div className="modal-actions">
              <div className="action-buttons-group">
              <button 
                  type="button" 
                  className="action-button cancel-button"
                  onClick={onClose}
              >
                  <Icon name="close" /> {t('todos.addModal.actions.cancel')}
              </button>
              <button 
                  type="button" 
                  className="action-button accept-button"
                  onClick={handleAccept}
              >
                  <Icon name="check" /> {t('ai.previewModal.actions.accept')}
              </button>
              </div>
          </div>
      )}
    </Modal>
  );
};

export default AIPreviewModal;
